import {
  MailTwoTone,
  MobileTwoTone,
  ArrowRightOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { Button } from '@mm/components';
import { MenuIcon } from '@mm/icons';
import { useFacebookErrorCRUDActions } from '@mm/state/actions/FacebookErrorCRUDActions';
import { useLiveChatCRUDActions } from '@mm/state/actions/LiveChatCRUDActions';
import JSONUtils from '@mm/state/helpers/json-utils';
import { preferredConversationName } from '@mm/state/helpers/live-chat-helpers';
import { LivechatConversationModel } from '@mm/state/model/LivechatConversationModel';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import {
  activeChatbotIdSelector,
  selectActiveCompanyId,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import noProfilePicIcon from 'assets/icons/contact_noimage.svg';
import { ReactComponent as EmptyListIcon } from 'assets/icons/empty-list-icon.svg';
import classNames from 'classnames';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useContextSelector } from 'use-context-selector';
import { FacebookPageErrors } from '../../../../../../common/components/FacebookPageAlerts/FacebookPageErrors';
import { ImageWithFallback } from '../../../../../../common/components/ImageWithFallback/ImageWithFallback';
import { CHATBOT_EDITOR } from '../../../../../../common/constants/routes';
import ObjectUtils from '../../../../../../common/helpers/utils/object-utils';
import { messageStructureParser } from '../../../../liveChat/components/Messages/messageStructureParser';
import { LiveChatContext } from '../../../../liveChat/LiveChatContext';
import { ActivationBlock } from '../ActivationBlock/ActivationBlock';
import { EducationalCardsBlock } from '../EducationalCardsBlock/EducationalCardsBlock';
import openLinkIcon from './open-link-icon.svg';
import css from './AutomationsDashboard.module.css';
import { FollowUpChats } from '../../../../../../layout/BotBuilderSidebar/common/TopLevelSidebarLink/FollowUpChats';
import { EmailAnalytics } from '../../Analytics/EmailAnalytics/EmailAnalytics';
import { Modal } from 'antd';
import howtoVideo from 'assets/howto-video.png';
import { useUsersCRUDActions } from '@mm/state/actions/UsersCRUDActions';
import {
  selectCurrentUser,
  selectCurrentUserId,
  selectSessionSelector,
} from '@mm/state/selectors/userSelector';
import { useResolutionInfo } from '@mm/hooks';

export const NewDashboard = () => {
  const livechatCrudActions = useLiveChatCRUDActions();

  const activeChatbotId = useSelector(activeChatbotIdSelector);

  const activeFacebookPage = useSelector(activeFacebookPageSelector);

  const baseLcUrl = `${CHATBOT_EDITOR}/${activeChatbotId}/live-chat`;

  const remotePageId = useSelector(
    (state: StoreState) => state.connectedPage.pageDetails?.remoteId || '',
  );

  const [filteredConversations, setFilteredConversations] = useState<
    LivechatConversationModel[]
  >([]);

  const [valuableConversationsCount, setValuableConversationsCount] =
    useState(0);

  // Note: initial placeholder for future functinality of multiple lead types
  const [selectedTab, setSelectedTab] = useState(1);
  const qualifiedLeadsTabSelected = selectedTab === 1;

  const history = useHistory();

  const lcService = useContextSelector(
    LiveChatContext,
    v => v.lcServiceRef?.current,
  );

  const lcMachineSend = lcService?.send;

  const { updateUserAttributes } = useUsersCRUDActions();
  const facebookErrorCRUDActions = useFacebookErrorCRUDActions();

  const session = useSelector(selectSessionSelector);
  const userAttributes = session?.userAttributes;

  const companyId = useSelector(selectActiveCompanyId);
  const currentUserId = useSelector(selectCurrentUserId);

  const [showWelcomeSmallModal, setShowWelcomeSmallModal] = useState(false);
  const [showWelcomeModalVideo, setShowWelcomeModalVideo] = useState(false);

  const iframeMouseOverRef = useRef(false);
  const { screenWidth } = useResolutionInfo();
  const [startedWatchingVideo, setStartedWatchingVideo] = useState(false);

  const onWindowBlur = () => {
    // Note: this allows us to track if the user clicked the 'play' button in the iframe
    if (iframeMouseOverRef.current) {
      setStartedWatchingVideo(true);
    }
  };

  useEffect(() => {
    if (companyId && currentUserId && startedWatchingVideo) {
      // Note: considering seen if the user has not just opened the modal, but also watched the video
      const timeoutHandle = setTimeout(() => {
        updateUserAttributes(companyId, currentUserId, {
          stopShowingWalkthroughVideo: true,
        });
      }, 2.5 * 60 * 1000); // Note: video is ~5 minutes, but users can watch it a faster speed

      return () => {
        clearTimeout(timeoutHandle);
      };
    }
  }, [companyId, currentUserId, startedWatchingVideo]);

  useEffect(() => {
    window.focus();
    window.addEventListener('blur', onWindowBlur);

    return () => {
      window.removeEventListener('blur', onWindowBlur);
    };
  }, []);

  useEffect(() => {
    if (
      !userAttributes.stopShowingWalkthroughVideo &&
      session?.sessionsCount &&
      session.sessionsCount < 4
    ) {
      setShowWelcomeSmallModal(true);
    }
    if (
      currentUserId &&
      companyId &&
      !userAttributes.stopShowingWalkthroughVideo &&
      session?.sessionsCount &&
      session.sessionsCount > 3
    ) {
      // Note: by agreement with BE team, FE decideds when to set the attribute to stop showing the video.
      updateUserAttributes(companyId, currentUserId, {
        stopShowingWalkthroughVideo: true,
      });
    }
  }, [
    userAttributes.stopShowingWalkthroughVideo,
    companyId,
    currentUserId,
    session?.sessionsCount,
  ]);

  useEffect(() => {
    if (activeFacebookPage?.id) {
      facebookErrorCRUDActions.retrieveAll(activeFacebookPage.id);
    }
  }, [activeFacebookPage?.id]);

  const params = {
    remotePageId,
    conversationState: 'all' as const,
    selectedTags: qualifiedLeadsTabSelected ? ['unicorn'] : [],
    page: 0,
    perPage: 6,
  };

  const getConversations = async params => {
    const res = await livechatCrudActions.getFilteredConversations(params);

    setFilteredConversations(res || []);
  };

  const getContactsCount = async params => {
    const res = await livechatCrudActions.getFilteredContactsCount(params);
    if (qualifiedLeadsTabSelected) {
      setValuableConversationsCount(res || 0);
    }
  };

  useEffect(() => {
    getConversations(params);
    getContactsCount(params);
  }, [remotePageId, selectedTab]);

  const seeValuableConversations = async () => {
    lcMachineSend?.({
      type: 'LC_VIEW_UPDATE',
      payload: {
        viewUpdate: {
          selectedTags: qualifiedLeadsTabSelected ? ['unicorn'] : [],
          selectedAssignee: 'all',
        },
      },
    });

    history.push(baseLcUrl);
  };

  const viewMessage = async (remoteUserId: string) => {
    lcMachineSend?.({
      type: 'LC_VIEW_UPDATE',
      payload: {
        viewUpdate: {
          selectedAssignee: 'all',
          selectedTags: qualifiedLeadsTabSelected ? ['unicorn'] : [],
        },
      },
    });

    setTimeout(
      () =>
        lcMachineSend?.({
          type: 'LC_REMOTE_USER_ID_CHANGE',
          payload: remoteUserId,
        }),
      50,
    );
    setTimeout(
      () => history.push(`${baseLcUrl}?remoteUserId=${remoteUserId}`),
      500,
    );
  };

  return (
    <div className={css.container}>
      <div className={css.errorsContainer}>
        <FacebookPageErrors />
      </div>

      {/* Note: not using our standard modals approach for this modal not to interfere with other modals,
      as we want to persistently show it in the corner of the page till the user watches it or the user is past first 3 sessions*/}
      <Modal
        className={css.welcomeModalSmall}
        destroyOnClose
        mask={false}
        style={{
          top: `calc(100vh - ${showWelcomeModalVideo ? '540px' : '360px'})`,
        }}
        visible={showWelcomeSmallModal && screenWidth > 700}
        title={
          <div className={css.welcomeModalTitle}>Welcome to Customers.ai!</div>
        }
        cancelText="Close"
        okText="Watch Short Video"
        closeIcon={<CloseOutlined style={{ color: 'white' }} />}
        cancelButtonProps={{
          style: {
            color: 'white',
            display: showWelcomeModalVideo ? 'block' : 'none',
            borderColor: 'white',
            backgroundColor: '#1a4975',
          },
        }}
        okButtonProps={{
          style: {
            display: showWelcomeModalVideo ? 'none' : 'block',
            color: 'white',
            borderColor: 'white',
            backgroundColor: '#1a4975',
          },
          disabled: showWelcomeModalVideo,
          ghost: true,
        }}
        onOk={() => {
          setShowWelcomeModalVideo(true);
          setStartedWatchingVideo(true);
        }}
        onCancel={() => {
          setShowWelcomeSmallModal(false);
        }}
        width={showWelcomeModalVideo ? 690 : 400}
      >
        {showWelcomeModalVideo ? (
          <div
            onMouseOver={() => {
              iframeMouseOverRef.current = true;
            }}
            onMouseOut={() => {
              window.focus();
              iframeMouseOverRef.current = false;
            }}
          >
            <iframe
              width="640"
              height="394"
              src="https://player.vimeo.com/video/856966839?h=4265e990d9&color=3454EF&title=0&byline=0&autoplay=1"
              allowFullScreen
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              title="X-Ray Pixel Email Capture System in 90 Seconds"
            />
          </div>
        ) : (
          <img
            src={howtoVideo}
            className={css.welcomeModalSmallImage}
            onClick={() => {
              setShowWelcomeModalVideo(true);
              setStartedWatchingVideo(true);
            }}
          />
        )}
      </Modal>

      <div className={css.channelsContainer}>
        <EducationalCardsBlock />
        <ActivationBlock />
      </div>

      <EmailAnalytics
        css={{
          container: css.topContainer,
          header: css.topContainerHeader,
          goToAutomationsButton: css.goToAutomationsButton,
        }}
        asAutomationRows
        header="My Automation Analytics"
        perPage={3}
        withPagination={false}
      />
      <div className={css.bottomContainer}>
        <div className={css.unicornInboxContainer}>
          <div className={css.heading}>Unicorn Inbox</div>
          <div
            className={classNames(css.inboxTabWrapper, {
              [css.selectedInboxTab]: selectedTab === 0,
            })}
            onClick={() => setSelectedTab(0)}
          >
            <MenuIcon
              className={css.inboxIcon}
              color={selectedTab === 0 ? '#2F54EB' : '#BFBFBF'}
            />
            All
          </div>
          <div
            className={classNames(css.inboxTabWrapper, {
              [css.selectedInboxTab]: qualifiedLeadsTabSelected,
            })}
            onClick={() => setSelectedTab(1)}
          >
            <MailTwoTone
              twoToneColor={
                qualifiedLeadsTabSelected ? '#2F54EB' : ['#BFBFBF', '#ecf0f6']
              }
              className={css.inboxIcon}
            />
            <span className={css.tabLabel}>
              Qualified Leads
              <FollowUpChats
                count={valuableConversationsCount}
                css={{ wrapper: css.followupChats }}
              />
            </span>

            <div className={css.inboxLinks} onClick={seeValuableConversations}>
              <img src={openLinkIcon} className={css.openLinkIcon} />
              Inbox
            </div>
          </div>
        </div>
        <div className={css.messagesContainer}>
          <div className={css.header}>
            {filteredConversations.length > 0 && (
              <div className={css.headerInfoTitle}>
                {qualifiedLeadsTabSelected
                  ? '🔥 Hottest New Unicorns'
                  : '🔥 Latest Conversations'}
              </div>
            )}
          </div>

          {filteredConversations.length ? (
            <>
              <div className={css.leads}>
                {filteredConversations.map((i, idx) => {
                  const { structure } = i;
                  const parsedStructure = JSONUtils.safeParse(structure);

                  let parsedMessage = 'Click Read to view the last message';

                  if (parsedStructure) {
                    const { getDisplayText } = messageStructureParser({
                      structure: ObjectUtils.deepCamelizeKeys(parsedStructure),
                    });

                    const textToDsiplay = getDisplayText();
                    if (textToDsiplay) {
                      parsedMessage = textToDsiplay;
                    }
                  }

                  const preferedName = preferredConversationName(i);

                  return (
                    <div key={idx} className={css.leadRow}>
                      <div className={css.leadMainInfo}>
                        <div className={css.leadRowIcon}>
                          <ImageWithFallback
                            src={i.profilePic}
                            fallbackImage={noProfilePicIcon}
                            alt={`${i.firstName} ${i.lastName}`}
                            className={css.leadRowIconImage}
                          />
                        </div>

                        <div className={css.leadRowInfo}>
                          <div className={css.leadRowInfoTitle}>
                            {preferedName}
                          </div>
                          <div className={css.leadRowInfoMessage}>
                            {parsedMessage}
                          </div>
                        </div>
                      </div>

                      <div className={css.leadRowAdditionalInfoContainer}>
                        <div className={css.leadRowAdditionalInfo}>
                          <MobileTwoTone
                            className={css.leadRowAdditionalInfoIcon}
                            twoToneColor="#BFBFBF"
                          />
                          {moment(Number(i.time)).calendar()}
                        </div>

                        <Button
                          color="transparent"
                          onClick={() => viewMessage(i?.remoteUserId || '')}
                          css={{
                            container: css.leadRowAdditionalActionButton,
                          }}
                        >
                          Read
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className={css.seeMore}>
                <Button
                  onClick={seeValuableConversations}
                  color="transparent"
                  css={{
                    container: css.leadRowAdditionalActionButton,
                  }}
                >
                  {qualifiedLeadsTabSelected
                    ? ' Go to Unicorn Leads'
                    : ' See All Leads'}
                  <ArrowRightOutlined className={css.seeMoreIcon} />
                </Button>
              </div>
            </>
          ) : (
            <div className={css.emptyContainer}>
              <EmptyListIcon />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
